import { Component, Input, OnInit, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, interval, Subscription, takeWhile } from 'rxjs';
import {
  isLargeScreen,
  getUserReadableDateTimeFormat,
  formatDateTimeStamp,
  getFormattedDate,
} from '../../../../global.variable';
import { UsersService } from '../../../pages/pages/users/users.service';
import { WebSocketService } from '../../services/web-socket.service';
import { ToasterService } from '../../services/toaster.service';
import { DeviceInfoService } from '../../services/device-info.service';
import { JobsService } from '../../../pages/pages/jobs/jobs.service';
import { ModalDialogueService } from '../modal-dialogue/modal-dialogue.service';
import { SubContactorsService } from '../../../pages/pages/sub-contractors/sub-contactors.service';

@Component({
  selector: 'gtapp-user-live-data',
  standalone: false,
  templateUrl: './user-live-data.component.html',
  styleUrl: './user-live-data.component.scss',
})
export class UserLiveDataComponent implements OnInit {
  @Input() selectedUser: any;
  @Input() routedToPage: boolean = true;

  largeView = isLargeScreen;
  userLiveDataList: any = [];
  userLiveDataStats: any = {};

  otherParamsUserLiveData: any = {
    apiFiltering: true,
    hideHeaders: !this.largeView,
  };
  previousUserLiveData: number = 0;
  rowsUserLiveData: number = this.largeView ? 10 : 7;
  pageNumUserLiveData: number = 1;
  sortKeyUserLiveData: any;

  searchParamsUserLiveData: any;

  userLiveTableInfo: any = [];
  jobsWithoutStatus: any = [];
  jobStatusData: any = {};

  webSocketSubscription: Subscription = new Subscription();

  filterByStats: any = new Set();

  statusDict: any = {
    1: 'Awaiting Clock In',
    2: 'Clocked In',
    3: 'Clocked In',
    4: 'Late Clock In',
    5: 'Clocked Out Early',
    6: 'Clocked Out',
  };
  liveDataInitialCount = 0;
  loggedInDeviceID = this.deviceInfoService.getUniqueDeviceID();

  responseArrived: any = {};

  mobileViewHomePage: boolean = false;
  moreItemsInMobileViewHomePage: boolean = false;

  mobileFilterStringDict: any = {};
  filterDataTimeOutFn: any;

  disableExportBtn: boolean = false;
  exportEnableRemSeconds: number = 0;
  exportDataTimeoutSubscription!: Subscription;
  readonly COOLDOWN_TIME = 60000;

  subContractorData = { data: [], backup: [] };

  constructor(
    private router: Router,
    private userService: UsersService,
    private webSocketService: WebSocketService,
    private toasterService: ToasterService,
    private deviceInfoService: DeviceInfoService,
    private jobService: JobsService,
    private dialogService: ModalDialogueService,
    private viewContainerRef: ViewContainerRef,
    private contractorService: SubContactorsService,
  ) {}
  ngOnDestroy() {
    this.webSocketSubscription?.unsubscribe();
    if (this.filterDataTimeOutFn) clearTimeout(this.filterDataTimeOutFn);
    if (this.exportDataTimeoutSubscription)
      this.exportDataTimeoutSubscription?.unsubscribe();
  }
  ngOnInit(): void {
    // mobile view home page show only 3 items
    this.mobileViewHomePage =
      !this.largeView && !this.selectedUser?.id && !this.routedToPage;

    if (!this.selectedUser?.id || this.routedToPage) {
      this.checkExportDisabling();
    }

    if (!this.loggedInDeviceID) {
      this.loggedInDeviceID = this.deviceInfoService.getUniqueDeviceID();
    }

    this.webSocketSubscription =
      this.webSocketService.webSocketSubject.subscribe((event: any) => {
        const webSocketSourceDevice = event?.data?.logged_in_device_id;

        // if the source is the same device no need to trigger anything for that
        if (webSocketSourceDevice == this.loggedInDeviceID) return;
        if (
          [
            'wsn_user_clock_in',
            'user_job_update',
            'wsn_update_roster_table',
            'wsn_user_clock_out',
            'wsn_scheduled_patrol_tasks_update',
          ].includes(event?.event)
        ) {
          setTimeout(() => {
            this.checkAndCallApis();
          }, 1000);
        }
      });
    this.checkAndCallApis();

    this.userLiveTableInfo = this.getTableDetails();
  }

  async checkAndCallApis() {
    if (this.mobileViewHomePage) {
      let reqBody: any = this.getRequestBodyDict();

      if (this.sortKeyUserLiveData) reqBody.sort_key = this.sortKeyUserLiveData;

      if (this.filterByStats) reqBody.filter_stats = [...this.filterByStats];
      if (this.selectedUser?.id) reqBody.sub_user_id = this.selectedUser?.id;
      if (this.searchParamsUserLiveData)
        reqBody.search_filters = this.searchParamsUserLiveData;

      reqBody.rows = 4;
      this.callUserDataApi(reqBody, {}).then((response: any) => {
        const responseData = response?.data || [];
        this.moreItemsInMobileViewHomePage = responseData?.length > 3;

        this.userLiveDataList = responseData?.slice(0, 3);
        this.liveDataInitialCount = this.userLiveDataList?.length;
      });

      // mobile view home page show only 3 items
      return;
    }
    let reqBody: any = this.getRequestBodyDict();
    if (this.filterByStats) reqBody.filter_stats = [...this.filterByStats];
    if (this.selectedUser?.id) reqBody.sub_user_id = this.selectedUser?.id;

    this.callUserDataApi(reqBody, { get_total_count: 1 }).then(
      (response: any) => {
        if (response['status'] == 'success') {
          this.otherParamsUserLiveData = {
            ...this.otherParamsUserLiveData,
            paginationData: {
              totalRows: response?.data,
              previousRows: this.previousUserLiveData,
              rows: this.rowsUserLiveData,
              pageNum: this.pageNumUserLiveData,
              fixedRows: true,
            },
          };
          if (response?.data) {
            this.liveDataInitialCount = response?.data;
            this.getUserLiveDataCount();
            this.getUserLiveData();
          } else {
            this.liveDataInitialCount = 0;
          }
        }
      },
    );
  }
  callMultipleApis() {
    this.getUserLiveData();
    // this.getUserLiveStats();
    this.getUserLiveDataCount();
  }
  onFilterByStatus(status: any) {
    if (this.filterByStats?.has(status)) {
      this.filterByStats.delete(status);
    } else {
      this.filterByStats.add(status);
    }

    this.getUserLiveData();
    this.getUserLiveDataCount();
  }

  getUserLiveDataCount() {
    let reqBody: any = this.getRequestBodyDict(true);

    this.callUserDataApi(reqBody, { get_total_count: 1 }).then(
      (response: any) => {
        if (response['status'] == 'success') {
          this.otherParamsUserLiveData = {
            ...this.otherParamsUserLiveData,
            paginationData: {
              totalRows: response?.data,
              previousRows: this.previousUserLiveData,
              rows: this.rowsUserLiveData,
              pageNum: this.pageNumUserLiveData,
              fixedRows: true,
            },
          };
        }
      },
    );
  }

  callUserDataApi(reqBody: any, reqParams: any) {
    return new Promise((resolve) => {
      this.userService
        .getUserUpdateLog(reqBody, reqParams)
        .subscribe((response: any) => {
          resolve(response);
        });
    });
  }
  getRequestBodyDict(applyFilters = false) {
    let reqBody: any = {
      event_type: 'user_live_jobs',
      // time_range: [lastMonth, new Date()],
    };
    // reqBody.time_range = this.getReportStartEndDateTime();
    if (applyFilters) {
      let searchFilters: any = {};
      if (this.filterByStats) reqBody.filter_stats = [...this.filterByStats];
      if (this.selectedUser?.id) reqBody.sub_user_id = this.selectedUser?.id;

      if (this.searchParamsUserLiveData)
        searchFilters = this.searchParamsUserLiveData;

      if (searchFilters) reqBody.search_filters = searchFilters;
    }
    return reqBody;
  }

  getUserLiveData() {
    let reqBody: any = this.getRequestBodyDict(true);

    if (this.rowsUserLiveData) reqBody.rows = this.rowsUserLiveData;
    if (this.previousUserLiveData) reqBody.previous = this.previousUserLiveData;
    if (this.sortKeyUserLiveData) reqBody.sort_key = this.sortKeyUserLiveData;

    this.callUserDataApi(reqBody, {}).then((response: any) => {
      if (response['status'] == 'success') {
        this.userLiveDataList = response?.data;
      } else {
        this.toasterService.setMessage({
          errorMessage: response['message'],
          successMessage: '',
        });
      }
    });
  }

  onChangePaginationUserLiveData(data: any) {
    this.previousUserLiveData = data?.paginationData?.previousRows;
    this.pageNumUserLiveData = data?.paginationData?.pageNum;

    this.rowsUserLiveData = data?.paginationData?.rows;

    this.otherParamsUserLiveData.paginationData = {
      ...data?.paginationData,
    };
    this.getUserLiveData();
  }
  searchColumnUserLiveData(event: any) {
    this.previousUserLiveData = 0;
    this.pageNumUserLiveData = 1;
    this.searchParamsUserLiveData = event?.searchParams || {};
    this.getUserLiveData();
    this.getUserLiveDataCount();
  }
  sortColumnUserLiveData(body: any) {
    this.sortKeyUserLiveData = body?.sortList || [];
    this.getUserLiveData();
  }
  routeToUserStatsORJob(jobData: any) {
    if (this.mobileViewHomePage && this.moreItemsInMobileViewHomePage) {
      this.router.navigate(['/operations-overview']);
      return;
    }
    if (jobData?.assignee_uuid && !this.selectedUser?.id) {
      this.router.navigate([`/user-stats`], {
        queryParams: { uId: jobData?.assignee_uuid },
      });
    } else {
      this.routeToJob(jobData);
    }
  }
  routeToJob(jobData: any) {
    if (jobData?.source == 'job') {
      this.router.navigate([`/job-detail/JOBID`], {
        queryParams: { jId: jobData?.id },
      });
    } else if (jobData?.source == 'runsheet') {
      this.router.navigate([
        '/view-runsheet',
        {
          rKey: String(jobData?.id),
        },
      ]);
    } else {
      this.router.navigate([
        '/view-route',
        {
          rKey: String(jobData?.id),
        },
      ]);
    }
  }
  getTableDetails() {
    const lateEarlyCheck = (row: any) => {
      if (
        [2, 3, 4].includes(row?.job_current_status) &&
        row?.last_clocked_in_at
      ) {
        //clock in statuses

        const clockedInAt = new Date(row?.last_clocked_in_at).getTime();
        const shiftStart = new Date(row.start_date_time).getTime();
        if (clockedInAt !== shiftStart) {
          let diffInTime = 0;
          let timeStat = 'early';
          if (clockedInAt > shiftStart) {
            timeStat = 'late';
            diffInTime = clockedInAt - shiftStart;
          } else {
            diffInTime = shiftStart - clockedInAt;
          }
          let minutes = Math.floor(diffInTime / 60000);

          return [
            minutes,
            timeStat,
            timeStat == 'early'
              ? 'var(--color-flurogreen)'
              : 'var(--bs-danger)',
          ];
        }
      } else if (
        [6, 5].includes(row?.job_current_status) &&
        row?.last_clocked_out_at
      ) {
        //clock out statuses
        const clockedOutAt = new Date(row?.last_clocked_out_at).getTime();
        const shiftEnd = new Date(row.end_date_time).getTime();
        if (clockedOutAt !== shiftEnd) {
          let diffInTime = 0;
          let timeStat = 'early';
          if (clockedOutAt > shiftEnd) {
            timeStat = 'late';
            diffInTime = clockedOutAt - shiftEnd;
          } else {
            diffInTime = shiftEnd - clockedOutAt;
          }
          let minutes = Math.floor(diffInTime / 60000);

          return [
            minutes,
            timeStat,
            timeStat == 'late' ? 'var(--color-flurogreen)' : 'var(--bs-danger)',
          ];
        }
      }

      return [null, null, null];
    };
    const getStatusValue = (row: any) => {
      let currentJobStatus: any;
      const status = row?.job_current_status;
      if (row?.is_job_complete) {
        if (status == 1) {
          // no need to show awaiting clock in if the shift is completed early and clocked out by user
          currentJobStatus = row?.job_status_name;
        } else {
          currentJobStatus = this.statusDict?.[status] || this.statusDict?.[1];
          if (row?.job_status_name)
            currentJobStatus += ` [${row?.job_status_name}]`;
        }
      } else {
        currentJobStatus = this.statusDict?.[status] || this.statusDict?.[1];
        if (row?.job_status_name)
          currentJobStatus += ` [${row?.job_status_name}]`;
      }

      return currentJobStatus;
    };
    const getStatusStyle = (row: any) => {
      //     statusDict: any = {
      //   1: 'Awaiting Clock In',
      //   2: 'Active',
      //   3: 'Clocked In',
      //   4: 'Late Clock In',
      //   5: 'Clocked Out Early',
      //   6: 'Clocked Out',
      // };
      const status = row?.job_current_status;
      let stlyeDict = {};
      switch (status) {
        case 1:
          if (row?.is_job_complete) {
            stlyeDict = {
              'color': '#0F6B0F',
              'background': '	#D1F2D1',
              'border-radius': '5px',
              'padding': '0 0.5rem',
              'border': '1px solid #D1F2D1',
            };
          } else {
            const isShiftStarted =
              new Date(row.start_date_time).getTime() < new Date().getTime();
            if (isShiftStarted) {
              stlyeDict = {
                'color': 'var(--bs-danger)',
                'background': '#FFF4CC',
                'border-radius': '5px',
                'padding': '0 0.5rem',
                'border': '1px solid #FFF4CC',
              };
            } else {
              stlyeDict = {
                'color': '#8A6A00',
                'background': '#FFF4CC',
                'border-radius': '5px',
                'padding': '0 1.5rem',
                'border': '1px solid #FFF4CC',
              };
            }
          }

          break;
        case 2: // Clocked In Onsite
          stlyeDict = {
            'color': '#0F6B0F',
            'background': '	#D1F2D1',
            'border-radius': '5px',
            'padding': '0 0.5rem',
            'border': '1px solid #D1F2D1',
          };
          break;
        case 3:
          stlyeDict = {
            'color': '#0F6B0F',
            'background': '	#D1F2D1',
            'border-radius': '5px',
            'padding': '0 0.5rem',
            'border': '1px solid #D1F2D1',
          };
          break;
        case 4:
          stlyeDict = {
            'background': '#FFE1E1',
            'border-radius': '5px',
            'padding': '0 0.5rem',
            'border': '1px solid #FFE1E1',
            'color': '#A30000',
          };
          break;
        case 5:
          stlyeDict = {
            'background': '#FFE8CC',
            'border-radius': '5px',
            'padding': '0 0.5rem',
            'border': '1px solid #FFE8CC',
            'color': '#8A4B00',
          };
          break;
        case 6:
          stlyeDict = {
            'color': '#555555',
            'background': '#F2F2F2',
            'border-radius': '5px',
            'padding': '0 0.5rem',
            'border': '1px solid #F2F2F2',
          };
          break;
      }
      return stlyeDict;
    };
    const tableDetails = this.largeView
      ? [
          {
            header: 'Site',
            name: 'site_name',
            searchKey: 'site_name',
            showItem: true,
          },
          // {
          //   // job type
          //   header: 'Job Type',
          //   name: 'job_type_name',
          //   searchKey: 'job_type_name',
          //   showItem: true,
          // },
          {
            // name
            header: 'Guard',
            name: 'assignee_name',
            searchKey: 'assignee_name',
            showItem: !this.selectedUser?.id,
          },
          {
            //status
            header: 'Status',
            nestedValue: (row: any) => {
              return getStatusValue(row);
            },

            conditionalColumnDataStyle: (row: any) => {
              let currentJobStatusStyle: any = {
                ...getStatusStyle(row),
                'font-weight': '600',
                'display': 'flex',
                'text-align': 'center',
                'align-items': 'center',
                'justify-content': 'center',
              };

              return currentJobStatusStyle;
            },
            suffixNestedValue: (row: any) => {
              const [minutes, timeStat, _] = lateEarlyCheck(row);
              if (minutes && timeStat) {
                return `[${minutes} mins ${timeStat}]`;
              }

              return null;
            },

            conditionalSuffixStyle: (row: any) => {
              const [minutes, timeStat, color] = lateEarlyCheck(row);
              if (color) {
                return {
                  'font-weight': '600',
                  'color': color,
                  'display': 'flex',
                  'align-items': 'center',
                  'justify-content': 'center',
                };
              }

              return {};
            },

            showItem: true,
          },
          {
            // time
            header: 'Start Time',
            nestedValue: (row: any) => {
              const startReadableFormat = getUserReadableDateTimeFormat(
                row?.start_date_time,
              );

              return (
                startReadableFormat +
                ' ' +
                formatDateTimeStamp(row?.start_date_time, 'HH:mm', 'en_US')
              );
            },

            showItem: true,
          },
          {
            // time
            header: 'End Time',
            nestedValue: (row: any) => {
              const endReadableFormat = getUserReadableDateTimeFormat(
                row?.end_date_time,
              );

              return (
                endReadableFormat +
                ' ' +
                formatDateTimeStamp(row?.end_date_time, 'HH:mm', 'en_US')
              );
            },

            showItem: true,
          },
        ]
      : [
          {
            name: 'assignee_name',
            tableColumnStyle: {
              'font-size': '18px',
              'font-weight': '400',
              'color': 'var(--text-secondary)',
              'white-space': 'pre-line',
              'vertical-align': 'top',
            },

            suffixNestedValue: (row: any) => {
              let suffixValues = ['', row?.name || row?.site_name];
              // if (row?.job_type_name) suffixValues.push(row?.job_type_name);

              return suffixValues?.join('\n');
            },
            suffixStyle: {
              'font-size': '13px',
            },
            showItem: true,
          },
          {
            nestedValue: (row: any) => {
              const startReadableFormat = getUserReadableDateTimeFormat(
                row?.start_date_time,
              );
              const endReadableFormat = getUserReadableDateTimeFormat(
                row?.end_date_time,
              );

              return (
                startReadableFormat +
                ' ' +
                formatDateTimeStamp(row?.start_date_time, 'HH:mm', 'en_US') +
                ' - ' +
                ((startReadableFormat != endReadableFormat
                  ? getUserReadableDateTimeFormat(row?.end_date_time) + ' '
                  : '') +
                  formatDateTimeStamp(row?.end_date_time, 'HH:mm', 'en_US'))
              );
            },
            tableColumnStyle: {
              'vertical-align': 'top',
              'font-size': '13px',
              'text-align': 'end',
            },
            columnDataStyle: {
              'display': 'flex',
              'justify-content': 'flex-end',
              'margin-bottom': '1rem',
            },

            suffixNestedValue: (row: any) => {
              const statusList = [getStatusValue(row)];
              const [minutes, timeStat, _] = lateEarlyCheck(row);
              if (minutes && timeStat) {
                statusList.push(`${minutes} mins ${timeStat}`);
              }
              return statusList?.join('\n');
            },
            conditionalSuffixStyle: (row: any) => {
              let currentJobStatusStyle: any = {
                ...getStatusStyle(row),
                'font-weight': '600',
                'text-align': 'center',
                'display': 'flex',
                'margin-left': 'auto',
                'width': 'fit-content',
                'white-space': 'pre-line',
              };

              return currentJobStatusStyle;
            },
            showItem: true,
          },
        ];

    return tableDetails?.filter((item: any) => item?.showItem);
  }
  filterData(filterSource: string, forceCallApi = false) {
    if (this.filterDataTimeOutFn) clearTimeout(this.filterDataTimeOutFn);

    const siteStringLength = this.mobileFilterStringDict?.site?.length;
    const guardStringLength = this.mobileFilterStringDict?.guard?.length;
    this.searchParamsUserLiveData = {
      ...(this.searchParamsUserLiveData || {}),
    };
    if (filterSource == 'site') {
      this.searchParamsUserLiveData['site_name__icontains'] =
        this.mobileFilterStringDict?.site;
    }
    if (filterSource == 'guard') {
      this.searchParamsUserLiveData['assignee_name__icontains'] =
        this.mobileFilterStringDict?.guard;
    }

    let callApi = siteStringLength > 2 || guardStringLength > 2;

    if (callApi || forceCallApi)
      this.filterDataTimeOutFn = setTimeout(() => {
        this.getUserLiveData();
        this.getUserLiveDataCount();
      }, 500);
  }

  async exportData(template: any) {
    this.getSubContractors('');
    const dRef = this.dialogService.open(
      template,
      {
        data: {
          report_date: getFormattedDate(),
          maxReportDate: getFormattedDate(),
          sub_contractor: {},
          job_type: {},
          report_exists: true,
        },
      },
      this.viewContainerRef,
    );
    const dRes = await firstValueFrom(dRef.afterClosed());
    if (!dRes) return;

    let reqBody: any = this.getRequestBodyDict();

    reqBody.time_range = this.getReportStartEndDateTime(dRes?.report_date);

    if (dRes?.sub_contractor?.id)
      reqBody.sub_contractor_id = dRes?.sub_contractor?.id;

    if (dRes?.job_type?.id) reqBody.job_type_dict = dRes?.job_type;

    const expiryTime = Date.now() + this.COOLDOWN_TIME;
    sessionStorage.setItem(
      btoa('overviewExportDisable'),
      expiryTime.toString(),
    );
    this.startCooldown(this.COOLDOWN_TIME);

    this.callUserDataApi(reqBody, { export_data: 1 }).then((response: any) => {
      if (response['status'] == 'success') {
        this.toasterService.setMessage({
          errorMessage: '',
          successMessage: response['message'],
        });
      } else {
        this.toasterService.setMessage({
          errorMessage: response['message'],
          successMessage: '',
        });
      }
    });
  }
  checkExportDisabling() {
    const savedExpiry = sessionStorage.getItem(btoa('overviewExportDisable'));
    if (savedExpiry) {
      const remainingTime = parseInt(savedExpiry, 10) - Date.now();
      if (remainingTime > 0) {
        this.startCooldown(remainingTime);
      } else {
        this.clearCooldown();
      }
    }
  }

  startCooldown(duration: number) {
    this.disableExportBtn = true;
    const startTime = Date.now();
    const totalDuration = duration;

    if (this.exportDataTimeoutSubscription) {
      this.exportDataTimeoutSubscription.unsubscribe();
    }

    this.exportDataTimeoutSubscription = interval(100)
      .pipe(takeWhile(() => this.disableExportBtn))
      .subscribe(() => {
        const elapsed = Date.now() - startTime;
        const remaining = totalDuration - elapsed;

        if (remaining <= 0) {
          this.clearCooldown();
        } else {
          this.exportEnableRemSeconds = Math.ceil(remaining / 1000);
        }
      });
  }

  clearCooldown() {
    this.disableExportBtn = false;
    this.exportEnableRemSeconds = 0;
    sessionStorage.removeItem(btoa('overviewExportDisable'));
    if (this.exportDataTimeoutSubscription) {
      this.exportDataTimeoutSubscription.unsubscribe();
    }
  }
  getSubContractors(event?: any) {
    let params: any = { rows: 10, order_by: 'company_name' };
    if (event && event?.target.value?.length < 2) return;

    if (event?.target?.value) {
      params['search_str'] = event?.target.value;
    }

    this.contractorService.getContactors(params).then((response: any) => {
      this.subContractorData.data = response?.data || [];
      if (!event) {
        this.subContractorData.backup = JSON.parse(
          JSON.stringify(response?.data || []),
        );
      }
    });
  }
  focusElement(elementId: string) {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) element.focus();
    }, 200);
  }

  async checkExportStatus(data: any) {
    data.report_exists = false;
    if (data?.report_date > data?.maxReportDate) return;

    data.editDate = false;
    let reqBody: any = this.getRequestBodyDict();

    reqBody.time_range = this.getReportStartEndDateTime(data?.report_date);

    if (data?.sub_contractor?.id)
      reqBody.sub_contractor_id = data?.sub_contractor?.id;

    if (data?.job_type?.id) reqBody.job_type_dict = data?.job_type;
    const response: any = await this.callUserDataApi(reqBody, {
      exists_check: 1,
    });

    data.report_exists = !!response?.data;
  }
  getReportStartEndDateTime(customDate?: any) {
    const reportDate = customDate ? new Date(customDate) : new Date();
    const reportStart = new Date(reportDate);
    reportStart.setHours(0, 0, 0, 0);
    const reportEnd = new Date(reportDate);
    reportEnd.setHours(23, 59, 59, 0);
    return [reportStart, reportEnd];
  }
}
